<template>
    <div class="search-input" @click.capture="handleWrapperClick">
        <AutoComplete
            ref="locationRef"
            v-model="locationInput"
            :class="{'without-border': withoutBorder}"
            :append-to="appendToSelf ? 'self' : 'body'"
            :disabled="disabled"
            :suggestions="locationSuggestions"
            option-label="formattedAddress"
            :aria-label="label"
            :placeholder="label"
            :empty-search-message="$t('generic.no.results.found')"
            :auto-option-focus="autoOptionFocus ?? false"
            :complete-on-focus="true"
            :force-selection="true"
            :delay="100"
            :data-testid="inputTestId"
            :input-class="[inputClass, {'without-icon': hideLocationIcon}]"
            :pt="pt"
            :show-clear="!hideClearableIcon && !disabled"
            :loading="suggestionLoading"
            @change="onLocationChange"
            @option-select="onLocationSelect($event, preventOptionSelection)"
            @complete="locationSearch"
        >
            <template #option="{index, option}">
                <div v-if="index === 0 && option.remoteAllowed" class="search-input__option search-input__option--with-icon">
                    <VJoyIcon name="home" />
                    <div>{{ option.formattedAddress }}</div>
                </div>
                <div v-else class="search-input__option">
                    <div>{{ option.formattedAddress }}</div>
                </div>
            </template>
            <template #loader>
                <VJoySpinner v-if="suggestionLoading" class="search-input__loader p-autocomplete-loader" />
                <span v-else />
            </template>
            <template #clearicon>
                <VJoyIcon size="xsmall" name="cross" class="p-autocomplete-clear-icon" @click="clearQuery" />
            </template>
        </AutoComplete>
        <VJoyIcon v-if="!hideLocationIcon" name="location" :color="placeholderIconColor ?? 'neutral'" />
    </div>
</template>

<script lang="ts" setup>
    import {computed, watch} from 'vue';
    import {type AutoCompletePassThroughOptions} from 'primevue/autocomplete';
    import {onMounted} from '#imports';
    import {VJoyIcon, VJoySpinner, type TJoyIconsColors} from '@maltjoy/core-vue';
    import {type Layer, type LayerRequest, useLocationAutocomplete} from '../../composables/useLocationAutocomplete';
    import type {Location} from '../../types/location';

    const props = defineProps<{
        /**
         * The pt object to override the default pt object
         */
        pt?: AutoCompletePassThroughOptions;
        /**
         * The color of the placeholder icon
         */
        placeholderIconColor?: TJoyIconsColors;
        /**
         * If `true` the first option will be focused automatically if exists
         */
        autoOptionFocus?: boolean;
        /**
         * If `true` the location icon will not be displayed
         */
        hideLocationIcon?: boolean;
        /**
         * If `true` the clearable icon will not be displayed
         */
        hideClearableIcon?: boolean;
        /**
         * The selected address value
         */
        modelValue: Location | null;
        /**
         * The label associated to the autocomplete input
         */
        label: string;
        /**
         * What layer to return when an address is found (the address "precision", sort of)
         */
        layer: Layer | LayerRequest[];
        /**
         * If `true` a "remote" option will be displayed as first suggestion when focusing on an empty input
         */
        remoteAllowed?: boolean;
        /**
         * If `true` the "remote" label will also include the name of the country
         */
        remoteByCountry?: boolean;
        /**
         * Optional `data-test-id` attribute to be set on the input
         */
        inputTestId?: string;
        /**
         * Field is invalid and show an error
         */
        invalid?: boolean;
        /**
         * Disabled html attribut
         */
        disabled?: boolean;
        /**
         * Add preventDefault at dropdown option item when selected, avoiding event capture problem with parent input
         */
        preventOptionSelection?: boolean;
        /**
         * Append to self or body
         */
        appendToSelf?: boolean;
        /**
         * Override the label used for the "remote" suggestion
         */
        remoteLabel?: string;
        /**
         * If `true` the input will not have a border
         */
        withoutBorder?: boolean;
    }>();

    const emits = defineEmits<{
        'update:modelValue': [value: Location | null];
    }>();

    const {
        locationInput,
        locationSuggestions,
        suggestionLoading,
        locationRef,
        selectedLocation,
        onLocationChange,
        onLocationSelect,
        locationSearch,
        remoteSuggestionLabel,
    } = useLocationAutocomplete({
        remoteAllowed: props.remoteAllowed ?? false,
        remoteByCountry: props.remoteByCountry ?? false,
        layer: props.layer ?? 'LOCALITY',
        remoteLabel: props.remoteLabel,
    });

    // it looks like passing an object triggers a prop validation error here
    const inputClass = computed(() => {
        if (!props.invalid) {
            return;
        }

        return 'search-input__invalid';
    });

    function handleWrapperClick(event: MouseEvent) {
        // fix primevue autocomplete issue with onContainerClick event
        if (!locationRef.value?.$el) {
            event.stopPropagation();
        }
    }

    function clearQuery() {
        locationInput.value = null;
        emits('update:modelValue', null);
    }

    function handleNewSelectedLocation(newSelectionLocation: Location | null) {
        selectedLocation.value = newSelectionLocation;
        if (!newSelectionLocation && locationInput.value && locationInput.value !== remoteSuggestionLabel) {
            locationInput.value = null;
        } else if (newSelectionLocation) {
            locationInput.value = newSelectionLocation.formattedAddress || null;
        }
    }

    watch(
        () => props.modelValue,
        (newSelectionLocation) => {
            if (newSelectionLocation !== selectedLocation.value) {
                handleNewSelectedLocation(newSelectionLocation);
            }
        },
    );

    watch(selectedLocation, (newValue) => {
        if (newValue !== props.modelValue) {
            emits('update:modelValue', newValue);
        }
    });

    function focus() {
        const input: HTMLInputElement | undefined = locationRef.value?.$el?.querySelector('input');
        if (input) {
            input.focus();
        }
    }

    onMounted(() => {
        handleNewSelectedLocation(props.modelValue);
    });

    defineExpose({
        focus,
    });
</script>

<style lang="scss" scoped>
    @use '../../internals/style/_search-input' as *;

    .search-input {
        .without-border {
            :deep(.p-inputtext) {
                border: none !important;
                box-shadow: none !important;
            }
        }

        &__loader {
            position: absolute;
            top: 12px;
            right: 11px;
        }
    }
</style>
